// RadicalToRationalPowerTask.js
// √[q](n) → a^(p/q), gdzie n = a^p (n JEST policzone w treści)
// Podstawa w poleceniu podana matematycznie (KaTeX).

import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import FractionInput from "../../../../components/inputs/FractionInput";

const pick = (arr) => arr[Math.floor(Math.random() * arr.length)];
const range = (a, b) => Array.from({ length: b - a + 1 }, (_, i) => a + i);

// Akceptuj: a^(p/q) i a^p/q (z ewentualnym minusem w liczniku)
function parseRationalPower(raw) {
  if (!raw) return null;
  const s = String(raw).replace(/\s/g, "");

  // a^(p/q)  lub a^(-p/q)
  let m = s.match(/^(\d+)\^\((-?\d+)\/(\d+)\)$/);
  if (m) return { base: parseInt(m[1],10), p: parseInt(m[2],10), q: parseInt(m[3],10) };

  // a^p/q  lub  a^-p/q
  m = s.match(/^(\d+)\^\-?(\d+)\/(\d+)$/);
  if (m) {
    const base = parseInt(m[1],10);
    const sign = s.includes("^-") ? -1 : 1;
    const p = sign * parseInt(m[2],10);
    const q = parseInt(m[3],10);
    return { base, p, q };
  }

  return null;
}

function prettyRationalLatex(raw) {
  if (!raw) return "";
  const s = String(raw).replace(/\s/g, "");
  let m = s.match(/^(\d+)\^\((-?\d+)\/(\d+)\)$/);
  if (m) return `${m[1]}^{\\tfrac{${m[2]}}{${m[3]}}}`;
  m = s.match(/^(\d+)\^\-?(\d+)\/(\d+)$/);
  if (m) {
    const base = m[1];
    const sign = s.includes("^-") ? "-" : "";
    return `${base}^{\\tfrac{${sign}${m[2]}}{${m[3]}}}`;
  }
  return s;
}

export function generateTask(options = {}) {
  const allowedBases = [2, 3, 4, 5, 6, 7, 8, 9];
  const requestedBase = Number(options.requiredBase);
  const base = allowedBases.includes(requestedBase)
    ? requestedBase
    : pick(allowedBases);
  const q = pick([2, 3, 4, 5]); // stopień pierwiastka

  const pMax = base === 2 ? 6 : base === 3 ? 4 : 3;

  // wybierz p tak, aby NIE było wielokrotnością q (unikamy „3/3”, „4/2” itp.)
  const candidates = range(1, pMax).filter((x) => x % q !== 0);
  const p = pick(candidates.length ? candidates : [1]); // awaryjnie 1

  // WAŻNE: w treści pokazujemy n = base^p JAKO LICZBĘ
  const n = Math.pow(base, p);
  const leftLatex = q === 2 ? `\\sqrt{${n}}` : `\\sqrt[${q}]{${n}}`;

  return {
    type: "RadicalToRationalPowerTask",
    data: { base, p, q, n, leftLatex }
  };
}

export function renderTask({ data }) {
  return (
    <div style={{ marginBottom: "12px" }}>
      Zapisz w postaci potęgi o podstawie <InlineMath math={`${data.base}`} />:{" "}
      <InlineMath math={data.leftLatex} />
    </div>
  );
}

export function renderInput(task, userAnswer, onChange, disabled) {
  const handleChange = (ans) => onChange?.(ans);

  return (
    <div>
      <FractionInput
        value={userAnswer}
        onChange={handleChange}
        disabled={disabled}
        // stały, ogólny placeholder – nie zdradza odpowiedzi
        placeholder={"np. 5^2/3 lub 5^-2/3"}
        label=""
        powerPreview={true}
      />
      <div aria-hidden style={{ marginTop: "-16px" }} />
      <div aria-hidden style={{ height: "12px" }} />
    </div>
  );
}

export function checkAnswer({ data }, userAnswer) {
  if (!userAnswer || typeof userAnswer !== "object" || !userAnswer.raw) return false;
  const parsed = parseRationalPower(userAnswer.raw);
  if (!parsed) return false;

  const { base, p, q } = data;
  if (parsed.base !== base) return false;

  // porównanie p/q (nie wymagamy skrócenia – liczymy krzyżowo)
  return (parsed.p * q === p * parsed.q);
}

// render w kanonie: a^{p/q} niezależnie od formatu wpisu
export function renderUserAnswer(userAnswer) {
  if (!userAnswer || typeof userAnswer !== "object" || !userAnswer.raw)
    return <i>(brak odpowiedzi)</i>;

  const parsed = parseRationalPower(userAnswer.raw);
  if (parsed) {
    const { base, p, q } = parsed;
    const latex = `${base}^{\\tfrac{${p}}{${q}}}`;
    return <InlineMath math={latex} />;
  }

  return <InlineMath math={prettyRationalLatex(userAnswer.raw)} />;
}

export function renderAnswer({ data }) {
  const { base, p, q } = data;
  return <InlineMath math={`${base}^{\\tfrac{${p}}{${q}}}`} />;
}

export function renderExplanation({ data }) {
  const { base, p, q, n } = data;
  const def = 'a^{\\frac{p}{q}}=\\sqrt[q]{\\,a^{p}\\,}\\quad (a>0,\\; q\\in\\mathbb{N}_{\\ge 2})';

  const toPower = `${q === 2 ? `\\sqrt{${n}}` : `\\sqrt[${q}]{${n}}`}\\;=\\;${base}^{\\tfrac{${p}}{${q}}}`;

  // opcjonalnie pokazujemy rozkład n = a^p jako wskazówkę (gdy p>1)
  const mul = p > 1 ? Array.from({ length: p }, () => `${base}`).join('\\cdot ') : null;
  const factorHint = p > 1
    ? `${n}\\;=\\;${base}^{${p}}\\;=\\;${mul}`
    : `${n}\\;=\\;${base}`;

  return (
    <div style={{ display: "grid", rowGap: "8px" }}>
      <p><strong>Krok 1:</strong> Rozpoznaj, że liczba pod pierwiastkiem to potęga podstawy.</p>
      <BlockMath math={factorHint} />
      <p><strong>Krok 2:</strong> Skorzystaj z definicji wykładnika wymiernego.</p>
      <BlockMath math={def} />
      <p><strong>Krok 3:</strong> Zapisz pierwiastek w postaci potęgi o wskazanej podstawie.</p>
      <BlockMath math={toPower} />
    </div>
  );
}
